<template>
  <fts-design-modal
    :backdrop="!submitting"
    :escape-event="false"
    :disabled-close="submitting"
    class="fts-widget-shortcut-design-modal"
    ref="ftsWidgetShortcutDesignModal"
  >
    <!-- title -->
    <template #title>
      <div class="design-modal-header-std__title">
        <font-awesome-icon :icon="['fas', 'share-from-square']" class="design-modal-header-icon" />
        <div class="design-modal-header-name design-modal-header-name--flex-1">
          Shortcut
        </div>
      </div>
    </template>
    <!-- description -->
    <template #description>
      <span class="design-modal-header-tag">Widget</span>
    </template>
    <!-- body -->
    <template #body>
      <!-- spinner -->
      <div class="design-modal-spinner" v-if="loading">
        <fts-spinner show />
      </div>
      <template v-else>
        <div class="d-widget-design-row">
          <div class="d-widget-design-row-text">
            Type
          </div>
          <!-- type -->
          <fts-form-element
            :form-data="formData"
            :item="{
              fieldName: 'type',
              label: false,
            }"
            :validator="$v.formData"
            comfort-design
            key="type"
          >
            <fts-select
              slot="formElement"
              slot-scope="{ formData, item, onInput, onBlur, error }"
              v-model="formData[item.fieldName]"
              :options="typeOptions"
              placeholder="Type"
              :error="!!error"
              comfort-design
              popper-prevent-overflow-alt-axis
              @input="onInput();updateType();"
              @search:blur="onBlur"
            />
          </fts-form-element>
        </div>

        <div class="d-widget-design-row">
          <div class="d-widget-design-row-text">
            Show
          </div>
          <!-- module -->
          <fts-form-element
            :form-data="formData"
            :item="{
              fieldName: 'moduleId',
              displayName: 'Module',
              label: false,
            }"
            :validator="$v.formData"
            comfort-design
            key="moduleId"
          >
            <fts-select
              slot="formElement"
              slot-scope="{ formData, item, onInput, onBlur, error }"
              v-model="formData[item.fieldName]"
              :options="modules"
              placeholder="Module"
              :error="!!error"
              comfort-design
              popper-prevent-overflow-alt-axis
              @input="onInput();updateModuleId();"
              @search:blur="onBlur"
            >
              <fts-label-group
                slot="selected-option-container"
                slot-scope="{ option }"
                :item-id="option.id"
                :list="modules"
                emoji
              />
              <fts-label-group
                slot="option"
                slot-scope="{ option }"
                :item-id="option.id"
                :list="modules"
                emoji
              />
            </fts-select>
          </fts-form-element>
        </div>
        
        <template v-if="formData.moduleId">
          <div
            class="d-widget-design-row"
            v-if="aggregateVisible"
          >
            <div class="d-widget-design-row-text">
              As
            </div>
            <div class="fts-col-4">
              <!-- aggregate -->
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'aggregate',
                  label: false,
                }"
                :validator="$v.formData"
                comfort-design
                key="aggregate"
              >
                <fts-select
                  slot="formElement"
                  slot-scope="{ formData, item, onInput, onBlur, error }"
                  v-model="formData[item.fieldName]"
                  :options="aggregateOptions"
                  placeholder="Aggregate"
                  :error="!!error"                  
                  comfort-design
                  popper-prevent-overflow-alt-axis
                  @input="onInput();updateAggregate()"
                  @search:blur="onBlur"
                />
              </fts-form-element>
            </div>
            <template v-if="informationIdVisible">
              <div class="d-widget-design-row-text">
                of
              </div>
              <!-- information -->
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'informationId',
                  displayName: 'Information',
                  label: false
                }"
                :validator="$v.formData"
                comfort-design
                class="d-widget-design-row-select"
                key="informationId"
              >
                <fts-select
                  slot="formElement"
                  slot-scope="{ formData, item, onInput, onBlur, error }"
                  v-model="formData[item.fieldName]"
                  label="displayName"
                  :options="informationIdOptions"
                  placeholder="Information"
                  :error="!!error"
                  comfort-design
                  popper-prevent-overflow-alt-axis
                  @input="onInput"
                  @search:blur="onBlur"
                >
                  <fts-label-group
                    slot="selected-option-container"
                    slot-scope="{ option }"
                    :item-id="option.id"
                    :list="informationIdOptions"
                    information
                  />
                  <fts-label-group
                    slot="option"
                    slot-scope="{ option }"
                    :item-id="option.id"
                    :list="informationIdOptions"
                    information
                  />
                </fts-select>
              </fts-form-element>
            </template>
          </div>

          <div
            class="d-widget-design-row"
            v-if="listingIdVisible"
          >
            <div class="d-widget-design-row-text">
              In
            </div>
            <!-- listing -->
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: 'listingId',
                displayName: 'Listing',
                label: false,
              }"
              :validator="$v.formData"
              comfort-design
              key="listingId"
            >
              <fts-select
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur, error }"
                v-model="formData[item.fieldName]"
                :options="listingOptions"
                placeholder="Listing"
                :error="!!error"
                comfort-design
                popper-prevent-overflow-alt-axis
                @input="onInput"
                @search:blur="onBlur"
              >
                <fts-label-group
                  slot="selected-option-container"
                  slot-scope="{ option }"
                  :item-id="option.id"
                  :list="listingOptions"
                  emoji
                />
                <fts-label-group
                  slot="option"
                  slot-scope="{ option }"
                  :item-id="option.id"
                  :list="listingOptions"
                  emoji
                />
              </fts-select>
            </fts-form-element>
          </div>

          <fts-collapsible-element
            label="Advanced"
            helper="Exclusive to alpha release."
            variant="style-1"
            comfort-design
            v-if="isInternal"
          >
            <template #content>
              <fts-form-element
                :form-data="formData"
                :item="{
                  type: 0,
                  fieldName: 'name',
                  displayName: 'Shortcut Name (optional)'
                }"
                comfort-design
                key="shortcutName"
              >
                <template #formLabel="{ label }">
                  {{label}} <fts-release-badge alpha class="fts-align-top" />
                </template>
              </fts-form-element>

              <fts-form-element
                :form-data="formData"
                :item="{fieldName: 'limit', input: 'number'}"
                comfort-design
                key="limit"
              >
                <template #formLabel="{ label }">
                  {{label}} <fts-release-badge alpha class="fts-align-top" />
                </template>
              </fts-form-element>
            </template>
          </fts-collapsible-element>
        </template>
      </template>
    </template>
    <!-- footer:left -->
    <template #footerLeft>
      <fts-btn
        text
        icon
        :disabled="submitting"
        @click="remove"
        v-if="isExist"
      >
        <font-awesome-icon :icon="['far', 'trash-alt']" />
      </fts-btn>
    </template>
    <!-- footer:right -->
    <template #footerRight>
      <!-- cancel -->
      <fts-btn
        text
        :disabled="submitting"
        @click="beforeClose"
      >
        Cancel
      </fts-btn>
      <!-- done -->
      <fts-btn
        outline
        color="primary"
        :loading="submitting"
        @click="done"
      >
        <font-awesome-icon :icon="['fas', 'check']" /> Ok
      </fts-btn>
    </template>
  </fts-design-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);
import { required, requiredIf } from "vuelidate/lib/validators";
import changesets from "diff-json";

import { authorizationStore } from "../../store/authorizationStore";
import { store as canvasDesignStore } from "../../store/canvasDesignStore";

import FTSCollapsibleElement from "../../elements/FTSCollapsibleElement";
import FTSDesignModal from "../../design/FTSDesignModal";
import FTSFormElement from "../../elements/FTSFormElement";
import FTSBtn from "../../elements/FTSBtn";
import FTSSelect from "../../elements/FTSSelect";
import FTSSpinner from "../../elements/FTSSpinner";
import FTSLabelGroup from "../../elements/FTSLabelGroup";

export default {
  components: {
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-design-modal": FTSDesignModal,
    "fts-form-element": FTSFormElement,
    "fts-btn": FTSBtn,
    "fts-select": FTSSelect,
    "fts-spinner": FTSSpinner,
    "fts-label-group": FTSLabelGroup,
  },
  props: {
    modules: Array,
    list: Array,
  },
  data: () => ({
    formData: null,
    originalData: null,
    resolve: null,
    loading: false,
    submitting: false,
    preloading: false,
    listingOptions: [],
    informationOptions: [],
    typeOptions: [
      { id: 'listingWithCount', name: 'Summary' },
      { id: 'addNew', name: 'Add New' },
      { id: 'listing', name: 'Listing' },
    ],
    aggregateOptions: [
      { id: 'count', name: 'Count' },
      { id: 'max', name: 'Maximum' },
      { id: 'min', name: 'Minimum' },
      // { id: 'average', name: 'Average' },
      { id: 'sum', name: 'Sum' },
    ],
  }),
  validations() {
    let formData = {
      type: { required },
      moduleId: { required },
      aggregate: {
        required: requiredIf(() => this.aggregateVisible)
      },
      informationId: {
        required: requiredIf(() => this.informationIdVisible)
      },
      listingId: {
        required: requiredIf(() => this.listingIdVisible)
      }
    };
    return { formData };
  },
  methods: {
    async show(formData) {
      this.formData = _.cloneDeep(formData);
      this.formData.widgetType = "shortcut";
      const valueToKeep = [
        'id', /* auto generate */
        'widgetType', /* default */
        'name',
        'type',
        'moduleId',
        'aggregate',
        'informationId',
        'listingId',
        'limit'
      ];
      for(var key in this.formData) {
        if (!valueToKeep.some(x => x == key)) {
          delete this.formData[key];
        }
      }
      this.loading = true;

      return new Promise(async (resolve) => {
        this.resolve = resolve;
        this.$refs.ftsWidgetShortcutDesignModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData);
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    },
                  },
                ],
              });
            } else {
              this.reset();
            }
          }
        });
        
        if (this.formData.moduleId) {
          if (typeof this.formData.moduleId === 'number') {
            this.$set(this.formData, "moduleId", this.formData.moduleId.toString());
          }
          await this.loadModule();
        }
        
        this.loading = false;
        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
        });
      });
    },
    beforeClose() {
      this.$refs.ftsWidgetShortcutDesignModal.beforeClose();
    },
    done() {
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        this.submitting = true;

        if (this.formData.informationId) {
          const information = this.informationOptions.find(x => x.id === this.formData.informationId);
          if (information) {
            information.displayName && this.$set(this.formData, "displayName", information.displayName);
            information.type && this.$set(this.formData, "informationType", information.type);
          }
        }

        const formData = _.cloneDeep(this.formData);
        this.resolve && this.resolve(formData);
      }
    },
    remove() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: `Delete Shortcut?`,
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.resolve && this.resolve(true);
              this.reset();
            },
          },
        ],
      });
    },
    reset() {
      this.$v.$reset();
      this.$refs.ftsWidgetShortcutDesignModal.close();
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    async loadModule() {
      this.listingOptions = [];
      this.informationOptions = [];
      if (this.formData.moduleId) {
        try {
          const res = await canvasDesignStore.dispatch("loadModule", this.formData.moduleId);
          if (res && res.data) {
            this.listingOptions = res.data.listings;
            this.informationOptions = res.data.information;
            const findModule = this.modules.find((x) => x.id == res.data.id);
            if (Array.isArray(findModule && findModule.children)) {
              this.listingOptions = this.listingOptions.filter((x) => findModule.children.some((y) => y.id == x.id));
            }
          }
        } catch(err) {
          this.$toasted.global.error({ response: err.response});
        }
      }
    },
    async updateModuleId() {
      this.$delete(this.formData, "listingId");
      this.$delete(this.formData, "informationId");
      if (this.formData.moduleId) {
        this.preloading = true;
        await this.loadModule();
        this.preloading = false;
      }
    },
    updateType() {
      this.$delete(this.formData, "aggregate");
      this.$delete(this.formData, "informationId");
      this.$delete(this.formData, "listingId");
    },
    updateAggregate() {
      this.$delete(this.formData, "informationId");
    },
  },
  computed: {
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    isExist() {
      return this.list && this.list.some(x => x.id === this.formData.id);
    },
    aggregateVisible() {
      return this.formData && this.formData.type === 'listingWithCount';
    },
    informationIdOptions() {
      if (this.formData.aggregate === 'max' || this.formData.aggregate === 'min') {
        return this.informationOptions.filter(x => [2, 3, 4, 5, 6].indexOf(x.type) > -1);
      } else {
        return this.informationOptions.filter(x => [2, 3, 4].indexOf(x.type) > -1);
      }
    },
    informationIdVisible() {
      return this.formData && this.formData.aggregate && this.formData.aggregate !== 'count';
    },
    listingIdVisible() {
      return (
        this.formData.type !== 'addNew' &&
        Boolean(this.listingOptions && this.listingOptions.length)
      );
    },
  }
}
</script>

<style lang="scss" scoped>
@use "./src/components/dashboard/design/scss/style";
</style>